/**
 * WordDocumentPage — DMS-managed Word document editing page.
 *
 * Phase 1: Download/upload workflow with checkout lock.
 * Phase 2: Will add OnlyOffice iframe when available (graceful upgrade).
 *
 * The user checks out a document, downloads the DOCX, edits in desktop Word,
 * then uploads the modified file to create a new version and release the lock.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  LockClosedIcon,
  LockOpenIcon,
  XMarkIcon,
  DocumentTextIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../../../api/axios';
import { useUIStore } from '../../../store/uiStore';

interface DocumentMeta {
  document_id: string;
  title: string;
  editor_type: string;
  client?: { client_id: string; name: string } | null;
  matter?: { matter_id: string; matter_number: string; title: string } | null;
  latest_version?: {
    version_id: string;
    version_number: string;
    mime_type: string;
    original_name: string;
    size_bytes: number;
    uploaded_at: string;
  } | null;
  is_checked_out: boolean;
  checked_out_by_user_id?: string | null;
  checked_out_at?: string | null;
  checked_out_expires_at?: string | null;
}

interface CheckoutStatus {
  isCheckedOut: boolean;
  checkedOutBy?: { user_id: string; keycloak_user_sub?: string; name: string } | null;
  checkedOutAt?: string | null;
  checkedOutExpiresAt?: string | null;
  hasWorkingCopy: boolean;
}

interface VersionItem {
  version_id: string;
  version_number: string;
  original_name: string;
  size_bytes: number;
  uploaded_at: string;
  uploaded_by_user_id: string;
  version_comment?: string;
}

export default function WordDocumentPage() {
  const params = useParams<{ documentId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setHeaderVisible = useUIStore((state: any) => state.setHeaderVisible);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const documentId = params.documentId || '';
  const versionIdFromUrl = searchParams.get('version') || undefined;
  const shouldAutoLaunchWord = searchParams.get('launch') === 'word';
  const isOfflinePreparation = searchParams.get('mode') === 'offline' || searchParams.get('offline') === '1';
  const autoLaunchAttemptedRef = useRef(false);

  // State
  const [doc, setDoc] = useState<DocumentMeta | null>(null);
  const [checkout, setCheckout] = useState<CheckoutStatus | null>(null);
  const [versions, setVersions] = useState<VersionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewObjectUrl, setPreviewObjectUrl] = useState<string | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [lastDownloadedFileName, setLastDownloadedFileName] = useState<string | null>(null);
  const [isCurrentUserCheckoutOwner, setIsCurrentUserCheckoutOwner] = useState(false);

  const normalizeCheckoutStatus = (payload: any): CheckoutStatus | null => {
    return payload?.checkoutStatus || payload || null;
  };

  const getCurrentKeycloakSubject = (): string | null => {
    const token = localStorage.getItem('webelieve_access_token');
    if (!token) return null;
    try {
      const [, payload] = token.split('.');
      if (!payload) return null;
      const decoded = JSON.parse(window.atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      return decoded?.sub || null;
    } catch {
      return null;
    }
  };

  const getActiveVersion = useCallback((): VersionItem | DocumentMeta['latest_version'] | null => {
    if (versionIdFromUrl) {
      const selectedVersion = versions.find((version) => version.version_id === versionIdFromUrl);
      if (selectedVersion) return selectedVersion;
      if (doc?.latest_version?.version_id === versionIdFromUrl) return doc.latest_version;
    }

    return doc?.latest_version || versions[0] || null;
  }, [doc?.latest_version, versionIdFromUrl, versions]);

  const getActiveVersionId = useCallback((): string | null => {
    return getActiveVersion()?.version_id || versionIdFromUrl || doc?.latest_version?.version_id || null;
  }, [doc?.latest_version?.version_id, getActiveVersion, versionIdFromUrl]);

  const getCheckoutDownloadFileName = useCallback((): string | null => {
    if (!doc) return null;
    const shortId = documentId.substring(0, 8);
    return `[DMS-${shortId}] ${doc.title}.docx`;
  }, [doc, documentId]);

  const stampWorkingCopyContext = async (blob: Blob, workingCopyContext: any): Promise<Blob> => {
    if (!workingCopyContext?.token) return blob;
    try {
      const JSZip = (await import('jszip')).default;
      const zip = await JSZip.loadAsync(await blob.arrayBuffer());
      const namespace = 'urn:edgelex:dms:working-copy:v1';
      const contextJson = JSON.stringify(workingCopyContext);
      const xml = [
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
        `<edgelexWorkingCopy xmlns="${namespace}">`,
        `  <json><![CDATA[${contextJson.replace(/\]\]>/g, ']]]]><![CDATA[>')}]]></json>`,
        '</edgelexWorkingCopy>',
      ].join('\n');

      zip.file('customXml/edgelex-working-copy.xml', xml);

      const relsPath = '_rels/.rels';
      const relsFile = zip.file(relsPath);
      if (relsFile) {
        const relsXml = await relsFile.async('string');
        if (!relsXml.includes('edgelex-working-copy.xml')) {
          const existingIds = Array.from(relsXml.matchAll(/Id="rId(\d+)"/g))
            .map((match) => Number(match[1]))
            .filter(Number.isFinite);
          const nextId = Math.max(0, ...existingIds) + 1;
          const rel = `  <Relationship Id="rId${nextId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/customXml" Target="customXml/edgelex-working-copy.xml"/>`;
          zip.file(relsPath, relsXml.replace('</Relationships>', `${rel}\n</Relationships>`));
        }
      }

      const stamped = await zip.generateAsync({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        compression: 'DEFLATE',
      });
      return stamped;
    } catch (stampError) {
      console.warn('[WordDocumentPage] Failed to stamp EdgeLex working-copy context:', stampError);
      return blob;
    }
  };

  const isCheckoutOwnedByCurrentUser = (checkoutData: CheckoutStatus | null, docData?: DocumentMeta | null): boolean => {
    if (!checkoutData?.isCheckedOut) return false;
    const currentSub = getCurrentKeycloakSubject();
    if (currentSub && checkoutData.checkedOutBy?.keycloak_user_sub === currentSub) return true;
    if (checkoutData.checkedOutBy?.user_id && docData?.checked_out_by_user_id) {
      return checkoutData.checkedOutBy.user_id === docData.checked_out_by_user_id;
    }
    return false;
  };

  // Hide global header while page is active
  useEffect(() => {
    setHeaderVisible(false);
    return () => { setHeaderVisible(true); };
  }, [setHeaderVisible]);

  // Load document data
  const loadDocument = useCallback(async () => {
    if (!documentId) return;
    try {
      const [docRes, versionsRes] = await Promise.all([
        apiClient.get(`/dms/documents/${documentId}`),
        apiClient.get(`/dms/documents/${documentId}/versions`).catch(() => ({ data: [] })),
      ]);

      const docData = docRes.data?.document || docRes.data?.documents?.[0] || docRes.data;
      setDoc(docData);

      const versionsList = versionsRes.data?.versions || versionsRes.data || [];
      const normalizedVersions = Array.isArray(versionsList) ? versionsList : [];
      setVersions(normalizedVersions);

      const activeVersionId =
        versionIdFromUrl ||
        docData?.latest_version?.version_id ||
        normalizedVersions[0]?.version_id;

      const checkoutRes = activeVersionId
        ? await apiClient
            .get(`/dms/documents/versions/${activeVersionId}/checkout-status`)
            .catch(() => apiClient.get(`/dms/documents/${documentId}/checkout-status`).catch(() => ({ data: null })))
        : await apiClient.get(`/dms/documents/${documentId}/checkout-status`).catch(() => ({ data: null }));

      const checkoutData = normalizeCheckoutStatus(checkoutRes.data);
      setCheckout(checkoutData);
      setIsCurrentUserCheckoutOwner(isCheckoutOwnedByCurrentUser(checkoutData, docData));

      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load document');
    } finally {
      setLoading(false);
    }
  }, [documentId, versionIdFromUrl]);

  useEffect(() => { loadDocument(); }, [loadDocument]);

  useEffect(() => {
    const activeVersionId = getActiveVersionId();
    if (!documentId || !activeVersionId) return;
    const storedFileName = localStorage.getItem(`word_checkout_download:${documentId}:${activeVersionId}`);
    if (storedFileName) setLastDownloadedFileName(storedFileName);
  }, [documentId, getActiveVersionId]);

  // Load preview
  useEffect(() => {
    const activeVersionId = getActiveVersionId();
    if (!activeVersionId) {
      setPreviewUrl(null);
      return;
    }

    let cancelled = false;

    const loadPreview = async () => {
      if (previewObjectUrl) {
        window.URL.revokeObjectURL(previewObjectUrl);
        setPreviewObjectUrl(null);
      }

      try {
        const response = await apiClient.get(
          `/dms/documents/${documentId}/versions/${activeVersionId}/preview`,
          { responseType: 'blob' }
        );
        if (cancelled) return;
        const contentType = response.headers?.['content-type'] || 'application/pdf';
        const blob = new Blob([response.data], { type: contentType });
        const objectUrl = window.URL.createObjectURL(blob);
        setPreviewObjectUrl(objectUrl);
        setPreviewUrl(objectUrl);
      } catch (err: any) {
        if (!cancelled) {
          setPreviewUrl(null);
          setError(err.response?.data?.message || err.message || 'Preview failed');
        }
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
    };
  }, [documentId, getActiveVersionId]);

  useEffect(() => {
    return () => {
      if (previewObjectUrl) {
        window.URL.revokeObjectURL(previewObjectUrl);
      }
    };
  }, [previewObjectUrl]);

  // --- Actions ---

  const handleCheckoutAndDownload = useCallback(async () => {
    const activeVersion = getActiveVersion();
    if (!doc || !activeVersion?.version_id) return;
    setActionLoading('checkout');
    try {
      const checkoutRes = await apiClient.post(`/dms/documents/versions/${activeVersion.version_id}/checkout`);
      // Download the file
      const response = await apiClient.get(`/dms/documents/${documentId}/versions/${activeVersion.version_id}/download`, {
        responseType: 'blob',
      });
      const rawBlob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const checkoutContext = checkoutRes.data?.workingCopyContext || (
        checkoutRes.data?.workingCopyToken ? { token: checkoutRes.data.workingCopyToken } : null
      );
      const blob = await stampWorkingCopyContext(rawBlob, checkoutContext ? {
        ...checkoutContext,
        title: doc.title,
        versionNumber: activeVersion.version_number,
      } : null);
      const filename = getCheckoutDownloadFileName() || activeVersion.original_name || `${doc.title}.docx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      localStorage.setItem(`word_checkout_download:${documentId}:${activeVersion.version_id}`, filename);
      setLastDownloadedFileName(filename);
      setIsCurrentUserCheckoutOwner(true);
      await loadDocument();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Checkout failed');
    } finally {
      setActionLoading(null);
    }
  }, [doc, documentId, getActiveVersion, getCheckoutDownloadFileName, loadDocument]);

  useEffect(() => {
    if (!shouldAutoLaunchWord || autoLaunchAttemptedRef.current || loading || !doc || !getActiveVersionId()) {
      return;
    }

    autoLaunchAttemptedRef.current = true;
    void handleCheckoutAndDownload();
  }, [doc, getActiveVersionId, handleCheckoutAndDownload, loading, shouldAutoLaunchWord]);

  const handleDownloadReadOnly = useCallback(async () => {
    const activeVersion = getActiveVersion();
    if (!doc || !activeVersion?.version_id) return;
    setActionLoading('download');
    try {
      const response = await apiClient.get(`/dms/documents/${documentId}/versions/${activeVersion.version_id}/download`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data]);
      const filename = activeVersion.original_name || `${doc.title}.docx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Download failed');
    } finally {
      setActionLoading(null);
    }
  }, [doc, documentId, getActiveVersion]);

  const handleUploadAndCheckin = useCallback(async () => {
    const activeVersionId = getActiveVersionId();
    if (!uploadFile || !doc || !activeVersionId) return;
    setActionLoading('upload');
    try {
      // Upload the file as a new version
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('version_number', getNextVersionNumber());
      formData.append('changes_description', 'Edited in Microsoft Word');

      // Upload file first
      const uploadRes = await apiClient.post('/dms/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const uploadedFile = uploadRes.data?.uploadedFile || uploadRes.data;

      // Create new version
      await apiClient.post(`/dms/documents/${documentId}/versions`, {
        uploadedFile,
        version_number: getNextVersionNumber(),
        changes_description: 'Edited in Microsoft Word',
      });

      // Check in
      await apiClient.post(`/dms/documents/versions/${activeVersionId}/checkin`, {
        createNewVersion: false, // Version already created above
        versionComment: 'Checked in from Microsoft Word',
      });

      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadDocument();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setActionLoading(null);
    }
  }, [uploadFile, doc, documentId, getActiveVersionId, loadDocument]);

  const handleDiscardCheckout = useCallback(async () => {
    const activeVersionId = getActiveVersionId();
    if (!activeVersionId) return;
    setActionLoading('discard');
    try {
      await apiClient.post(`/dms/documents/versions/${activeVersionId}/checkin`, {
        createNewVersion: false,
        versionComment: 'Checkout discarded',
      });
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadDocument();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Discard failed');
    } finally {
      setActionLoading(null);
    }
  }, [getActiveVersionId, loadDocument]);

  const handleUploadFileSelected = (file?: File | null) => {
    if (!file) return;
    const lowerName = file.name.toLowerCase();
    if (!lowerName.endsWith('.docx') && !lowerName.endsWith('.doc')) {
      setError('Please select the edited Word file (.docx or .doc).');
      return;
    }
    setUploadFile(file);
    setError(null);
  };

  const getNextVersionNumber = (): string => {
    if (versions.length === 0) return 'v1.0';
    const latest = versions[0]?.version_number || 'v0.0';
    const match = latest.match(/v?(\d+)\.(\d+)/);
    if (match) {
      return `v${match[1]}.${parseInt(match[2]) + 1}`;
    }
    return `v${versions.length + 1}.0`;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleString();
  };

  // --- Render ---

  if (!documentId) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-900">
        <p className="text-red-500">Error: Invalid document ID</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-sm text-gray-500 dark:text-gray-400">Loading document...</div>
      </div>
    );
  }

  const checkedOutByMe = checkout?.isCheckedOut && isCurrentUserCheckoutOwner;
  const checkedOutByOther = checkout?.isCheckedOut && !isCurrentUserCheckoutOwner;
  const activeVersion = getActiveVersion();

  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="flex items-center gap-4 px-6 py-3 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <button
          onClick={() => navigate('/documents')}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-blue-600 text-white text-xs font-bold">W</span>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
              {doc?.title || 'Untitled Document'}
            </h1>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {doc?.client && <span>{doc.client.name}</span>}
            {doc?.matter && <span>/ {doc.matter.matter_number}</span>}
            {activeVersion && (
              <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 font-medium">
                {activeVersion.version_number}
              </span>
            )}
          </div>
        </div>

        {/* Checkout Status Badge */}
        <div className="flex items-center gap-2">
          {checkout?.isCheckedOut ? (
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
              checkedOutByMe
                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
            }`}>
              <LockClosedIcon className="w-3.5 h-3.5" />
              {checkedOutByMe ? 'Checked out by you' : `Locked by ${checkout.checkedOutBy?.name || 'another user'}`}
            </div>
          ) : (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
              <LockOpenIcon className="w-3.5 h-3.5" />
              Available
            </div>
          )}
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mx-6 mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-center gap-2">
          <ExclamationTriangleIcon className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-300 flex-1">{error}</p>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {isOfflinePreparation && (
        <div className="mx-6 mt-4 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-3">
            <ArrowDownTrayIcon className="w-5 h-5 text-blue-600 dark:text-blue-300 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                Offline working copy flow
              </p>
              <p className="mt-1 text-sm text-blue-800 dark:text-blue-200">
                EdgeLex will prepare a signed Word working copy. Word Save only saves your local file; return here and use Upload & Check In to create the official DMS version.
              </p>
              {checkout?.checkedOutExpiresAt && (
                <p className="mt-2 text-xs font-medium text-blue-700 dark:text-blue-300">
                  Auto-release lock: {formatDate(checkout.checkedOutExpiresAt)}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Document Preview + Actions */}
        <div className="flex-1 flex flex-col items-center p-6 overflow-y-auto">
          {/* Preview */}
          <div className="w-full max-w-3xl bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
            {previewUrl ? (
              <iframe
                src={previewUrl}
                className="w-full"
                style={{ height: '600px' }}
                title="Document Preview"
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-96 text-gray-400 dark:text-gray-500">
                <DocumentTextIcon className="w-16 h-16 mb-4" />
                <p className="text-sm">Preview not available</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="w-full max-w-3xl">
            {!checkout?.isCheckedOut && (
              /* Available State — show checkout + read-only download */
              <div className="flex gap-3">
                <button
                  onClick={handleCheckoutAndDownload}
                  disabled={!!actionLoading}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <LockClosedIcon className="w-4 h-4" />
                  {actionLoading === 'checkout' ? 'Preparing Word file...' : 'Open in Word'}
                </button>
                <button
                  onClick={handleDownloadReadOnly}
                  disabled={!!actionLoading}
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
                >
                  <ArrowDownTrayIcon className="w-4 h-4" />
                  {actionLoading === 'download' ? 'Downloading...' : 'Download Only'}
                </button>
              </div>
            )}

            {checkedOutByMe && (
              /* Checked out by current user — show upload + discard */
              <div className="space-y-3">
                {/* File picker */}
                <div
                  className="p-4 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50"
                  onDragOver={(event) => {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = 'copy';
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    handleUploadFileSelected(event.dataTransfer.files?.[0]);
                  }}
                >
                  <div className="mb-3">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                      Word Save only saved your local working copy.
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Upload it here to create the official EdgeLex DMS version and check it in.
                    </p>
                    {lastDownloadedFileName && (
                      <p className="mt-2 text-xs text-blue-700 dark:text-blue-300">
                        Expected file: <span className="font-mono">{lastDownloadedFileName}</span>
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="inline-flex items-center justify-center rounded-lg bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300"
                    >
                      Choose edited file
                    </button>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {uploadFile ? uploadFile.name : 'or drag the edited DOCX here'}
                    </span>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".docx,.doc,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
                    onChange={(e) => handleUploadFileSelected(e.target.files?.[0])}
                    className="hidden"
                  />
                </div>

                {uploadFile && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 px-1">
                    Selected: {uploadFile.name} ({formatFileSize(uploadFile.size)})
                  </p>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={handleUploadAndCheckin}
                    disabled={!uploadFile || !!actionLoading}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ArrowUpTrayIcon className="w-4 h-4" />
                    {actionLoading === 'upload' ? 'Uploading...' : 'Upload & Check In'}
                  </button>
                  <button
                    onClick={handleDiscardCheckout}
                    disabled={!!actionLoading}
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 font-medium hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4" />
                    {actionLoading === 'discard' ? 'Discarding...' : 'Discard Checkout'}
                  </button>
                </div>
              </div>
            )}

            {checkedOutByOther && (
              /* Checked out by another user */
              <div className="p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-center">
                <LockClosedIcon className="w-6 h-6 text-yellow-600 dark:text-yellow-400 mx-auto mb-2" />
                <p className="text-sm text-yellow-800 dark:text-yellow-300 font-medium">
                  This document is currently checked out by {checkout?.checkedOutBy?.name || 'another user'}
                </p>
                {checkout?.checkedOutExpiresAt && (
                  <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                    Auto-release lock: {formatDate(checkout.checkedOutExpiresAt)}
                  </p>
                )}
                <button
                  onClick={handleDownloadReadOnly}
                  disabled={!!actionLoading}
                  className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-yellow-300 dark:border-yellow-700 text-yellow-700 dark:text-yellow-300 text-sm font-medium hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors"
                >
                  <ArrowDownTrayIcon className="w-4 h-4" />
                  Download (Read Only)
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar — Version History */}
        <div className="w-72 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-y-auto">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Version History</h3>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {versions.length === 0 ? (
              <p className="p-4 text-xs text-gray-400 dark:text-gray-500">No versions yet</p>
            ) : (
              versions.map((v) => {
                const isActive = v.version_id === activeVersion?.version_id;
                return (
                <div
                  key={v.version_id}
                  className={`p-3 hover:bg-gray-50 dark:hover:bg-gray-750 ${
                    isActive ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-blue-500' : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                      {v.version_number}
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {formatFileSize(v.size_bytes)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {v.original_name}
                  </p>
                  <div className="flex items-center gap-1 mt-1 text-xs text-gray-400 dark:text-gray-500">
                    <ClockIcon className="w-3 h-3" />
                    {formatDate(v.uploaded_at)}
                  </div>
                  {v.version_comment && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic">
                      {v.version_comment}
                    </p>
                  )}
                </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
